// Phase I0.5 — FixtureCard.
// One card in the DailyFixtureRail. Renders league eyebrow + status
// pill, two TeamLine rows (with kit-monogram fallback), and a settled
// summary line for finished fixtures. Live and settled scores are
// derived from the parent fixture row's hyphenated score string.

import type { TodayFixture } from "../../super-acca/useTodayFixtures";
import { FixtureStatusPill } from "./FixtureStatusPill";
import { TeamLine } from "./TeamLine";

export interface FixtureCardProps {
  fix: TodayFixture;
  active: boolean;
  onClick: () => void;
}

function splitScore(s: string | undefined, idx: 0 | 1): string | null {
  if (!s) return null;
  return s.split("-")[idx] ?? null;
}

export function FixtureCard({ fix, active, onClick }: FixtureCardProps) {
  const isLive = fix.status === "live";
  const isFinished = fix.status === "settled";
  const homeScore = isLive
    ? splitScore(fix.liveScore, 0)
    : isFinished
      ? splitScore(fix.finalScore, 0)
      : null;
  const awayScore = isLive
    ? splitScore(fix.liveScore, 1)
    : isFinished
      ? splitScore(fix.finalScore, 1)
      : null;
  const homeDim =
    isFinished && homeScore !== null && awayScore !== null && Number(homeScore) < Number(awayScore);
  const awayDim =
    isFinished && homeScore !== null && awayScore !== null && Number(awayScore) < Number(homeScore);

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        flex: "0 0 280px",
        scrollSnapAlign: "start",
        textAlign: "left",
        background: active ? "var(--sa-bg-raised)" : "var(--sa-bg-panel)",
        border: `1px solid ${active ? "var(--sa-accent-saffron)" : "var(--sa-hairline)"}`,
        borderRadius: 6,
        padding: 16,
        cursor: "pointer",
        position: "relative",
        transition: "all .18s",
        boxShadow: active ? "0 0 0 3px var(--sa-amber-glow)" : "none",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 12,
        }}
      >
        <span
          style={{
            fontFamily: "var(--sa-font-mono)",
            fontSize: 9,
            color: "var(--sa-fg-muted)",
            letterSpacing: "0.18em",
          }}
        >
          ◆ {fix.league.toUpperCase()}
        </span>
        <FixtureStatusPill status={fix.status} kickoff={fix.kickoff} minute={fix.minute} />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <TeamLine
          shortCode={fix.homeShort}
          name={fix.home}
          kitColor={fix.homeKit}
          badgeUrl={fix.homeBadge}
          score={homeScore}
          dim={homeDim}
        />
        <TeamLine
          shortCode={fix.awayShort}
          name={fix.away}
          kitColor={fix.awayKit}
          badgeUrl={fix.awayBadge}
          score={awayScore}
          dim={awayDim}
        />
      </div>
      {isFinished ? (
        <div
          style={{
            marginTop: 12,
            fontFamily: "var(--sa-font-mono)",
            fontSize: 10,
            color: "var(--sa-fg-muted)",
            letterSpacing: "0.16em",
          }}
        >
          ✓ SETTLED{fix.finalScore ? ` · ${fix.finalScore}` : ""}
        </div>
      ) : null}
    </button>
  );
}
